import {
  COMPACT_GRID_WIDTH,
  HEADER_HEIGHT,
} from '@act/react-components/app/constants';
import { useMobile } from '@act/utilities/mobile-context';
import {
  Card,
  CardActions,
  CardContent,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  useTheme,
} from '@mui/material';
import { reemaColors } from './colors';

export const FullScreenDialog = ({
  open,
  onClose,
  children,
  noElevation = false,
}: {
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
  noElevation?: boolean;
}) => {
  const { isMobile } = useMobile();

  // Always use COMPACT_GRID_WIDTH to match the care portal sidebar (sideMenuAlwaysCompact)
  const marginLeft = isMobile ? 0 : COMPACT_GRID_WIDTH;
  const width = `calc(100vw - ${marginLeft})`;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen
      hideBackdrop
      PaperProps={{
        sx: {
          marginLeft: marginLeft,
          width: width,
          transition: 'margin-left 0.5s ease-in-out, width 0.5s ease-in-out',
          ...(noElevation ? { boxShadow: 'none' } : {}),
          pointerEvents: 'auto',
        },
      }}
      sx={{
        pointerEvents: 'none',
        '& .MuiModal-backdrop': {
          marginLeft: marginLeft,
          transition: 'margin-left 0.5s ease-in-out',
          pointerEvents: 'none',
        },
        '& .MuiDialog-container': {
          pointerEvents: 'none',
        },
      }}
    >
      {children}
    </Dialog>
  );
};

export const FullScreenDialogTitle = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const theme = useTheme();

  return (
    <DialogTitle
      sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: '44px',
        padding: '18px 36px',
        zIndex: '2',
        elevation: 0,
        boxSizing: 'border-box',
        height: HEADER_HEIGHT,
        borderBottom: `1px solid ${theme.palette.strokePrimary}`,
      }}
    >
      {children}
    </DialogTitle>
  );
};

export const FullScreenDialogContent = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  return (
    <DialogContent
      sx={{
        display: 'grid',
        justifyContent: 'center',
        paddingTop: '12px !important',
        backgroundColor: reemaColors.lightBackground,
        zIndex: '1',
      }}
    >
      {children}
    </DialogContent>
  );
};

export const FullScreenCard = ({ children }: { children: React.ReactNode }) => {
  return (
    <Card
      sx={{
        height: 'min-content',
        width: 'calc(100vw - 65px)',
        maxWidth: '570px',
      }}
    >
      {children}
    </Card>
  );
};

export const FullScreenCardContent = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  return (
    <CardContent
      sx={{
        padding: '24px 24px 0',
        '&:last-child': {
          paddingBottom: 0,
        },
      }}
    >
      {children}
    </CardContent>
  );
};

export const FullScreenCardActions = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  return (
    <>
      <Divider sx={{ marginTop: '48px' }} />
      <CardActions
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'right',
          padding: '8px 24px',
        }}
      >
        {children}
      </CardActions>
    </>
  );
};
